/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { IAdminClozeData, TAdminClozeType } from '@datatype/admin/api-type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as adminActions } from '@store/slice/admin-slice';
import { useAdminClozesQuery } from '@hook/admin/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import ClozePreview from '@component/admin/cloze/cloze-home/cloze-preview';
import ClozeFilter from '@component/admin/cloze/cloze-home/cloze-filter';
import ClozeList from '@component/admin/cloze/cloze-home/cloze-list';
import ClozeMetricPanel from '@component/admin/cloze/cloze-home/cloze-metric-panel';

const PER_PAGE = 20;

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

const MetricPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`;

const CenterPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 600px;
  height: 100%;
  overflow-y: auto;
`;

const RightPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  flex: 1;
  height: 100%;
  overflow-y: auto;
`;

const ClozeHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const selectedClozeKey =
    useTypedSelector((state) => state.rootReducer?.adminReducer?.selectedClozeKey) ?? null;
  const filter = useTypedSelector(
    (state) =>
      state.rootReducer?.adminReducer?.clozeFilter ?? {
        clozeType: null,
        languageCode: null,
      },
  );
  const { clozeType, languageCode } = filter;

  const centerPaneRef = useRef<HTMLDivElement | null>(null);

  const clozesQuery = useAdminClozesQuery({
    perPage: PER_PAGE,
    clozeType,
    languageCode,
  });

  const clozes = clozesQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [];

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: clozesQuery.hasNextPage ?? false,
    isFetchingNextPage: clozesQuery.isFetchingNextPage ?? false,
    fetchNextPage: clozesQuery.fetchNextPage,
    root: centerPaneRef,
  });

  const selectedCloze: IAdminClozeData | null = useMemo(() => {
    if (!selectedClozeKey) return null;
    return clozes.find((c) => c.key === selectedClozeKey) ?? null;
  }, [clozes, selectedClozeKey]);

  const handleSelect = (cloze: IAdminClozeData) => {
    dispatch(adminActions.storeSelectedClozeKey(cloze.key));
  };

  const handleClozeTypeChange = (ct: TAdminClozeType | null) => {
    dispatch(adminActions.storeClozeFilter({ clozeType: ct }));
  };

  const handleLanguageChange = (lc: EApiLanguageCode | null) => {
    dispatch(adminActions.storeClozeFilter({ languageCode: lc }));
  };

  return (
    <Wrapper>
      <MetricPane>
        <ClozeMetricPanel />
      </MetricPane>
      <CenterPane ref={centerPaneRef}>
        <ClozeFilter
          clozeType={clozeType}
          languageCode={languageCode}
          handleClozeTypeChange={handleClozeTypeChange}
          handleLanguageChange={handleLanguageChange}
        />
        <ClozeList
          clozes={clozes}
          selectedClozeKey={selectedClozeKey}
          isLoading={clozesQuery.isLoading}
          isFetchingNextPage={clozesQuery.isFetchingNextPage ?? false}
          isError={clozesQuery.isError}
          handleSelect={handleSelect}
          handleRetry={clozesQuery.refetch}
          sentinelRef={sentinelRef}
        />
      </CenterPane>
      <RightPane>
        <ClozePreview selectedCloze={selectedCloze} />
      </RightPane>
    </Wrapper>
  );
};

export default ClozeHome;